/**
 * framedByJgCatalog.js — Framed by JG (Jacob Guthrie) price list
 *
 * A Santa Barbara luxury listing photo and film studio, used as the second
 * column of the quote builder's Competition analysis. Read from the price
 * tables in their booking page's JS (https://jacobguthrie.com/book, the chunk
 * holding `prices:[800,950`). Checked 2026-11-04, unchanged since 2026-10-04.
 *
 * Their sizes are inclusive upper bounds: up to 2,000 / 3,000 / 4,000 / 5,000 /
 * 6,000 / 7,000 / 8,000 sq ft, then "Inquire". Their square footage counts
 * ADUs, guest houses, garages and gyms, so a home of the same size can land a
 * rung higher with them than with us.
 *
 * Pricing models, as their form adds them up:
 * - `tiers`: one price per size rung, null above 8,000 sq ft (they inquire).
 * - `flat`: one price at any size.
 * - `perImage`: `flat` per image (their form prices it "$50/image").
 * - `kind`: which add-on group a service opens on their form ('photo' or
 *   'video').
 * - `addon`: sold only beside one of their services, never on its own; the
 *   value names the group it hangs off: 'photo' add-ons show only once their
 *   Photography is picked, 'video' add-ons once any video is, 'any' (their
 *   Extras) beside anything.
 */

export const JG_AS_OF = '2026-11-04';

export const JG_TIER_LABELS = [
  'Up to 2,000 sq ft', '2,001 – 3,000 sq ft', '3,001 – 4,000 sq ft', '4,001 – 5,000 sq ft',
  '5,001 – 6,000 sq ft', '6,001 – 7,000 sq ft', '7,001 – 8,000 sq ft',
];

export const JG_PRODUCTS = [
  { id: 'photography', name: 'Photography', kind: 'photo',
    desc: 'Editorial HDR stills, flash-and-ambient blended. Aerial drone included.',
    tiers: [800, 950, 1200, 1350, 1500, 1650, 1800] },
  { id: 'film', name: 'Cinematic Listing Film', kind: 'video',
    desc: 'A full cinematic property film: drone, sound design, world-class editing.',
    tiers: [1800, 1950, 2100, 2250, 2400, 2550, 2700] },
  { id: 'basicreel', name: 'Basic Social Reel', kind: 'video',
    desc: 'Vertical walkthrough reel, social-ready. No agent on camera.',
    tiers: [800, 1000, 1200, 1400, 1600, 1800, 2000] },
  { id: 'luxreel', name: 'Luxury Social Reel', kind: 'video',
    desc: 'Cinematic reel with agent on camera and lifestyle elements.',
    tiers: [2000, 2200, 2400, 2600, 2800, 3000, 3200] },
  { id: 'basicvideo', name: 'Basic Property Video', kind: 'video',
    desc: 'Straightforward walkthrough video covering the property practically.',
    tiers: [800, 1000, 1200, 1400, 1600, 1800, 2000] },
  // Photo add-ons
  { id: 'tw_photo', name: 'Twilight Photography', flat: 350, addon: 'photo' },
  { id: 'verticals', name: 'Verticals / Vignettes', flat: 350, addon: 'photo' },
  { id: 'ai_twilight', name: 'AI Twilights', flat: 150, addon: 'photo' },
  { id: 'vstaging', name: 'Virtual Staging', flat: 50, perImage: true, addon: 'photo' },
  // Video add-ons
  { id: 'tw_video', name: 'Twilight Video / Day-to-Night', flat: 350, addon: 'video' },
  { id: 'vcrop', name: 'Vertical Reel Crop', flat: 200, addon: 'video' },
  { id: 'agent', name: 'Agent On-Camera', flat: 150, addon: 'video' },
  { id: 'life_video', name: 'Lifestyle', flat: 200, addon: 'video' },
  // Extras: plans and tours, beside any service
  { id: 'floorplan', name: '2D Floor Plan', flat: 100, addon: 'any' },
  { id: 'zillow', name: 'Zillow 3D Tour', tiers: [300, 400, 500, 600, 700, 800, 900], addon: 'any' },
];

/** Size rung for a square footage, or null above 8,000 sq ft (they inquire). */
export function jgTierIndex(sqft = 0) {
  const n = Math.floor(Number(sqft) || 0);
  if (n <= 2000) return 0;
  if (n > 8000) return null;
  return Math.ceil(n / 1000) - 2;
}

export function findJgProduct(id) {
  return JG_PRODUCTS.find((p) => p.id === id) ?? null;
}

/** Their price for one unit at a size, or null where they inquire. */
export function jgGetPrice(product, sqft = 0) {
  if (product.flat !== undefined) return product.flat;
  const t = jgTierIndex(sqft);
  return t == null ? null : product.tiers[t];
}
